import { Box, Button, Stack, SxProps } from "@mui/material";
import { useTemplateContext } from "./TemplateContext";
import { TemplateData, TemplateModel, useCreateTemplate, useGetTemplateCategories, useSaveTemplate } from "./templates";
import { useEffect, useMemo, useState } from "react";

import { CreateNewButton } from "./CreateNewButton";
import { SaveAndContinueButton } from "./SaveAndContinueButton";

import { useQueryClient } from "react-query";
import { toast } from "react-toastify";
import FormSelect from "../../atoms/FormSelect/FormSelect";
import html2canvas from "html2canvas";
import { FormLabel } from "../../atoms";
import { FileUploader } from "../../molecules";
import TemplateNameModal from "./TemplateNameModal";
import { takeScreenshot } from "./takeScreenshot";

type TemplateEditFormStateType = {
  logoUrl?: string;
  topImage?: string;
  templateId?: number;
  name?: string;
  screenshot?: string;
  categoryId?: any;
  openNameModal: boolean;
  saveType?: "Create" | "Save";
};
type TemplateEditFormProps = {
  sx?: SxProps;
};
export function TemplateEditForm({ sx }: TemplateEditFormProps) {
  const {
    state: { template, onComplete, isOrder, isAdmin },
  } = useTemplateContext();
  const { data: categories } = useGetTemplateCategories();
  const queryClient = useQueryClient();
  const [state, setState] = useState({ categoryId: "", name: "" } as TemplateEditFormStateType);

  useEffect(() => {
    const data: TemplateEditFormStateType = {
      categoryId: template?.categoryId || "",
      name: template?.name || "",
      templateId: template?.id,
      openNameModal: false,
    };

    setState(data);
  }, [template]);

  const { mutate: createTemplate, isLoading: isCreating } = useCreateTemplate();
  const { mutate: saveTemplate, isLoading: isSaving } = useSaveTemplate();
  var isLoading = useMemo(() => isSaving || isCreating, [isSaving, isCreating]);

  const handleSave = () => {
    handleStateChange({ saveType: "Save", openNameModal: true });
  };

  const handleCreateNew = () => {
    handleStateChange({ saveType: "Create", openNameModal: true });
  };
  const completeSave = (name: string) => {
    const message1 = document.querySelector("#TextFieldOne")?.innerHTML;
    const message2 = document.querySelector("#TextFieldTwo")?.innerHTML;
    takeScreenshot(template).then((screenShot) => {
      const model = { ...state, name, screenShot, message1, message2 } as TemplateModel;
      if (state.saveType === "Create") {
        createTemplate(model, {
          onSuccess: (data) => {
            completed(data);
          },
        });
      } else {
        saveTemplate(model, {
          onSuccess: (data) => {
            completed(data);
          },
        });
      }
    });
  };

  useEffect(() => {
    if (!state.topImage) return;
    const image = document.querySelector("img#ImageFieldOne");
    if (image) {
      image.setAttribute("src", state.topImage!);
    }
    const bgImage = document.querySelector("table#ImageFieldOne");
    if (bgImage) {
      (bgImage as HTMLElement).style.backgroundImage = `url(${state.topImage!})`;
    }
  }, [state.topImage]);

  useEffect(() => {
    if (!state.logoUrl) return;
    const image = document.querySelector("#LogoPlaceholder");
    if (image) {
      image.setAttribute("src", state.logoUrl!);
    }
  }, [state.logoUrl]);

  const completed = (data: TemplateData) => {
    toast.success("Template saved successufully.");
    onComplete(data.id, data.imageUrl);
    queryClient.invalidateQueries("templates");
  };

  const handleStateChange = (params) => {
    setState((prev) => ({ ...prev, ...params }));
  };
  const closeNameModal = () => {
    handleStateChange({ openNameModal: false });
  };
  return (
    <>
      <Box
        sx={{
          borderRadius: "12px",
          padding: "1.875rem 24px",
          backgroundColor: "primary.main",
          alignSelf: "start",
          position: "absolute",
          top: 0,
          right: 0,
        }}
        className="template-edit-form"
      >
        <Stack spacing={2}>
          <FormSelect label="Category" options={categories} value={state.categoryId} onChange={(e) => setState((prev) => ({ ...prev, categoryId: e.target.value }))} />

          <FileUploader onChange={(files) => handleStateChange({ logoUrl: files })} renderControl={(render) => <BrowseImageButton title="Change Logo" onClick={render.onClick} />} />

          <FileUploader onChange={(files) => handleStateChange({ topImage: files })} renderControl={(render) => <BrowseImageButton title="Change Image" onClick={render.onClick} />} />

          <Stack spacing={2}>
            {isAdmin && <CreateNewButton onComplete={handleCreateNew} isLoading={isLoading} />}
            <SaveAndContinueButton onComplete={handleSave} isLoading={isLoading} />
          </Stack>
        </Stack>
      </Box>
      {state.openNameModal && (
        <TemplateNameModal
          handleClose={closeNameModal}
          name={state.name}
          onChange={(name) => {
            completeSave(name);
            closeNameModal();
          }}
        />
      )}
    </>
  );
}
type BrowseImageButtonProps = {
  onClick: () => void;
  title: string;
};
const BrowseImageButton = ({ onClick = () => {}, title }: BrowseImageButtonProps) => {
  return (
    <div>
      <FormLabel label={title} error={false} />
      <Button
        onClick={() => onClick()}
        sx={{
          color: "#ffffff",
          height: "3rem",
          borderRadius: "0.75rem",
          borderColor: "text.disabled",
          "&:hover": {
            borderColor: "text.secondary",
          },
          mt: "0.625rem",
          justifyContent: "flex-start",
        }}
        variant="outlined"
        fullWidth
      >
        Browse Image
      </Button>
    </div>
  );
};
